import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EXPENSE_CATEGORIES, INCOME_CATEGORIES } from "@/lib/entities";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const EMPTY = {
  bank_account_id: "",
  date: new Date().toISOString().slice(0, 10),
  direction: "debit",
  amount: "",
  description: "",
  vendor: "",
  invoice_no: "",
  suggested_category: "Other",
};

export function BankTxnDialog({ open, onOpenChange, txn, accounts, onSaved }) {
  const editing = !!txn;
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(
      txn
        ? {
            bank_account_id: txn.bank_account_id || "",
            date: (txn.date || "").slice(0, 10),
            direction: txn.direction || "debit",
            amount: String(txn.amount ?? ""),
            description: txn.description || "",
            vendor: txn.vendor || "",
            invoice_no: txn.invoice_no || "",
            suggested_category: txn.suggested_category || "Other",
          }
        : { ...EMPTY, bank_account_id: accounts[0]?.id || "" }
    );
  }, [open, txn, accounts]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.bank_account_id) return toast.error("Pick a bank account");
    if (!(Number(form.amount) > 0)) return toast.error("Enter an amount greater than zero");
    setBusy(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (editing) {
        await api.put(`/recon/bank-txns/${txn.id}`, payload);
        toast.success("Bank transaction updated · balance adjusted");
      } else {
        await api.post("/recon/bank-txns", payload);
        toast.success("Bank transaction added · ready for matching");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the transaction");
    } finally {
      setBusy(false);
    }
  };

  const cats = form.direction === "credit" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]" data-testid="bank-txn-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit bank transaction" : "Add bank transaction"}</DialogTitle>
          <DialogDescription>
            The bank balance is adjusted by the difference. The line stays unmatched until you
            confirm an invoice or book it directly.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Bank account</Label>
              <Select value={form.bank_account_id} onValueChange={set("bank_account_id")}>
                <SelectTrigger data-testid="txn-account-select">
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => set("date")(e.target.value)}
                data-testid="txn-date-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Money direction</Label>
              <Select value={form.direction} onValueChange={set("direction")}>
                <SelectTrigger data-testid="txn-direction-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="debit">Debit — money out</SelectItem>
                  <SelectItem value="credit">Credit — money in</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Amount</Label>
              <Input
                type="number"
                inputMode="decimal"
                value={form.amount}
                onChange={(e) => set("amount")(e.target.value)}
                placeholder="0.00"
                className="font-mono"
                data-testid="txn-amount-input"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Narration as it appears in the bank</Label>
            <Input
              value={form.description}
              onChange={(e) => set("description")(e.target.value)}
              placeholder="NEFT DR ACME LTD INV AC-2201"
              data-testid="txn-description-input"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5 col-span-1">
              <Label className="text-xs">Party</Label>
              <Input
                value={form.vendor}
                onChange={(e) => set("vendor")(e.target.value)}
                placeholder="Vendor / client"
                data-testid="txn-vendor-input"
              />
            </div>
            <div className="space-y-1.5 col-span-1">
              <Label className="text-xs">Invoice no</Label>
              <Input
                value={form.invoice_no}
                onChange={(e) => set("invoice_no")(e.target.value)}
                placeholder="optional"
                data-testid="txn-invoice-input"
              />
            </div>
            <div className="space-y-1.5 col-span-1">
              <Label className="text-xs">Likely head</Label>
              <Select value={form.suggested_category} onValueChange={set("suggested_category")}>
                <SelectTrigger data-testid="txn-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {cats.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="txn-cancel-btn">
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy} className="gap-2" data-testid="txn-save-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {editing ? "Save changes" : "Add transaction"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
